import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
    selectAnyInterTenantUserId,
    selectAppAllowConference,
    selectAuthUserByUuid,
    selectCurrentUserId,
    selectInterUserTenants,
    selectUsersDictionary,
    startCall
} from '../../../redux/slices';
import { useTypedDispatch, useTypedSelector } from '../../../redux/hooks';
import { isUser } from '../../../types';
import Icon from '../../../components/Icon';
import { usePolyglot } from '../../../context/Polyglot';
import ContactProvider, { useContactContext } from '../../../context/ContactContext/context';
import { StyledButton } from '../../../components/StyledComponents';
import { useCallContact } from '../../../components/sidebars/Contact/hooks';
import CallIconButton from '../../../components/buttons/CallIconButton';
import { useGetInterTenantClientUsersQuery } from '../../../redux/services/sipApi';
import { useUserStatusContext } from '../../home/children/UserStatusContext';
import { useConversationContext } from '../../../context/ChatContext/contextConvo';
import { ConversationIcon } from '../../../components/sidebars/chat/Conversation/ConversationIcon';
import { selectChatMembersOnChat, setActiveConversation } from '../../../redux/slices/chatNSlice';
import { useConference } from '../../../components/keypad/ConferenceHooks';
import { useOnClickOutside } from '../../../helpers';
import { useConversationName } from '../../../components/sidebars/chat/Conversation/ConversationRow';

interface IChatHeaderProps { }

interface IChatStatusProps {
    chatStatus?: {
        connected: boolean;
        last_seen?: number;
        status?: string;
    };
}

const ChatHeaderGroupCall: React.FC = () => {
    const { conversation } = useConversationContext();

    const participants = useTypedSelector(state => selectChatMembersOnChat(state, conversation.uuid)) || [];

    const userUuid = useTypedSelector(selectCurrentUserId);
    const usersDict = useTypedSelector(selectUsersDictionary);

    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    const groupCallRef = useRef<HTMLDivElement>(null);

    const { commitToRoom } = useConference()
    const { t } = usePolyglot();

    useOnClickOutside(groupCallRef, () => setShowConfirm(false))

    const handleGroupCall = () => {
        const invitationNumbers: string[] = []
        participants.forEach((m) => {
            if (m.member !== userUuid) {
                const foundExt = usersDict[m.member]?.extension;
                if (foundExt) {
                    invitationNumbers.push(String(foundExt));
                }
            }
        })

        commitToRoom({ invitationNumbers })
    }

    return (
        <div className='chat__header-group-call' ref={groupCallRef}>
            <CallIconButton
                handleCall={() => {
                    setShowConfirm(!showConfirm)
                }}
                ignore_transfer
            />
            {showConfirm ? (
                <div>
                    <p>
                        {t("actions.confirm_group_call")}
                    </p>
                    <CallIconButton
                        handleCall={() => {
                            handleGroupCall()
                        }}
                        ignore_transfer
                    />
                </div>
            ) : null}
        </div>
    )
}

const ChatHeaderCallBtn: React.FC = () => {
    const { contact } = useContactContext();

    const { callContact } = useCallContact();

    return (
        <CallIconButton
            handleCall={() => callContact()}
            user={
                isUser(contact)
                    ? {
                        uuid: contact.uuid,
                        name: contact.name
                    }
                    : undefined
            }
        />
    );
};

const UserCallBtn: React.FC<{ userUuid: string }> = ({ userUuid }) => {
    const { addNameForReq } = useUserStatusContext();

    const user = useTypedSelector(state => selectAuthUserByUuid(state, userUuid));

    const { client, handleCallClient } = useInterTenantPerson(
        !user ? userUuid : undefined
    );

    useEffect(() => {
        const returnFunc = () => {
            if (client) {
                addNameForReq(client.user_sip_name, false);
            }
            if (user) {
                addNameForReq(user.name, false);
            }
        };

        if (!client && !user) return returnFunc;

        if (client) {
            addNameForReq(client.user_sip_name, true);
        }
        if (user) {
            addNameForReq(user.name, true);
        }
        return returnFunc;
    }, [client, user]);

    if (!user) {
        if (client) {
            return (<CallIconButton
                handleCall={() => handleCallClient()}
                user={{
                    uuid: client.user_uuid,
                    name: client.user_sip_name
                }}
            />)
        }

        return null
    }

    return (
        <ContactProvider value={{ contact: user }}>
            <ChatHeaderCallBtn />
        </ContactProvider>
    );
}

export const ChatStatus: React.FC<IChatStatusProps> = ({ chatStatus }) => {
    const { connected, last_seen, status } = chatStatus || { connected: false };

    const TODAY = moment();
    const YESTERDAY = moment().subtract(1, 'days');
    const A_WEEK_OLD = moment().subtract(7, 'days');

    const { t } = usePolyglot();

    let statusFormatted: string;

    if (connected) {
        statusFormatted = t('phrases.presence_online');
    } else if (moment.unix(last_seen || 0).isSame(TODAY, 'day')) {
        statusFormatted = t('phrases.last_seen_today_at_%time', {
            time: moment.unix(last_seen || 0).format('HH:mm')
        });
    } else if (moment.unix(last_seen || 0).isSame(YESTERDAY, 'day')) {
        statusFormatted = t('phrases.last_seen_yesterday_at_%time', {
            time: moment.unix(last_seen || 0).format('HH:mm')
        });
    } else if (moment.unix(last_seen || 0).isAfter(A_WEEK_OLD, 'day')) {
        statusFormatted = t('phrases.last_seen_%day_at_%time', {
            day: moment.unix(last_seen || 0).format('dddd'),
            time: moment.unix(last_seen || 0).format('HH:mm')
        });
    } else {
        statusFormatted = t('phrases.last_seen_%day', {
            day: moment.unix(last_seen || 0).format('dddd')
        });
    }

    return (
        <>
            {last_seen ? (
                <p className={status ? '' : 'animation--none'}>{statusFormatted}</p>
            ) : (
                <p className={status ? '' : 'animation--none'}>{status || statusFormatted}</p>
            )}
        </>
    );
};

const useInterTenantPerson = (user_uuid: string | undefined) => {
    const tenants = useTypedSelector(selectInterUserTenants);

    const itm = useTypedSelector(state => selectAnyInterTenantUserId(state, user_uuid));

    const lst = Object.values(tenants);

    const tnt = lst.find(t => !t.users);

    const dispatch = useTypedDispatch();

    const handleCallClient = () => {
        if (itm) {
            dispatch(
                startCall(
                    `${itm.prefix}${itm.client.extension}`,
                    itm.client.user_display_name,
                    undefined,
                    itm.tenant
                )
            );
        }
    };

    useGetInterTenantClientUsersQuery(
        {
            tenant_uuid: tnt?.uuid ?? ''
        },
        {
            skip: !tnt || !user_uuid || !!itm,
            refetchOnMountOrArgChange: true
        }
    );

    return {
        client: itm?.client,
        handleCallClient
    };
};

export const ChatHeader: React.FC<IChatHeaderProps> = () => {
    const {
        conversation,
        sideBarVisible,
        useChatAction
    } = useConversationContext();
    const conferenceAllowed = useTypedSelector(selectAppAllowConference);
    const conversationName = useConversationName(conversation);

    const { t } = usePolyglot();

    const dispatch = useTypedDispatch();

    const handleSidebarToggle = () => {
        useChatAction({
            type: 'set_sidebar_visible',
            payload: !sideBarVisible
        });
    };

    // const userStatus = useTypedSelector(state =>
    // selectUserStatus(state, client?.user_sip_name || chateeUser?.name)
    // );

    // const currentRecipientChatStatus = userStatus?.chat || { connected: false };


    return (
        <div className='chat__header'>
            <div className='chat__header-details'>
                {conversation && (
                    <ConversationIcon
                        // chat={parentChat || chat}
                        convo={conversation}
                    />
                )}
                <div className='chat__header-name'>
                    <div>
                        <h1 className='chat_title'>
                            {conversationName}
                            {/*
                            {isIntegration(chat) && chat.integration?.number ? (
                                <span>&nbsp;&nbsp;{chat.integration.number}</span>
                            ) : null}
                                */}
                        </h1>
                        {/*
                        {!noPriorChatHistory && chat.type === 'user' && (
                            <div>
                                {currentRecipientChatStatus &&
                                    currentRecipientChatStatus.connected && (
                                        <ChatStatus chatStatus={currentRecipientChatStatus} />
                                    )}
                            </div>
                        )}
                            */}
                    </div>
                </div>
            </div>
            <div className='chat__header-options'>
                <StyledButton
                    buttonStyle='text'
                    iconOnly
                    context='danger'
                    onClick={() => dispatch(setActiveConversation(""))}
                    title={t('actions.close_chat')}
                >
                    <Icon name='cross' />
                </StyledButton>
                {conversation.type === 'user' ? (
                    <UserCallBtn userUuid={conversation.uuid} />
                ) : null}
                {conversation.type === 'channel' && conferenceAllowed ? <ChatHeaderGroupCall /> : null}
                <StyledButton
                    buttonStyle='text'
                    iconOnly
                    onClick={() => handleSidebarToggle()}
                    data-is-sidebar-toggle='true'
                    title={t('phrases.extra_options')}
                >
                    <Icon name='threeDots' />
                </StyledButton>
            </div>
        </div >
    );
};
